import { useRef, useState } from "react";
import { ImagePlus, Save, Wand2, Loader2, Sparkles, Trash2, RotateCcw, X, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import CouponCard from "@/components/hola/CouponCard";
import api from "@/lib/api";
import { compressImage } from "@/lib/compressImage";
import { toast } from "sonner";

const STYLES = [
  { id: "colorido", label: "Colorido", hint: "Festivo, promocional" },
  { id: "juvenil", label: "Juvenil", hint: "Neón, stickers" },
  { id: "sobrio", label: "Sobrio", hint: "Minimal, editorial" },
  { id: "elegante", label: "Elegante", hint: "Premium, dorado" },
];

const EMPTY = { title: "", description: "", image: "", discount_type: "percent", discount_value: 10, product_price: "", valid_from: "", valid_until: "", conditions: "", welcome: false, active: true, headline: "" };

export default function CouponEditor({ merchant, coupon, quota, onSaved, onClose, onQuota }) {
  const [form, setForm] = useState(coupon ? {
    title: coupon.title, description: coupon.description, image: coupon.image_original || "", discount_type: coupon.discount_type,
    discount_value: coupon.discount_value, product_price: coupon.product_price ?? "", valid_from: coupon.valid_from, valid_until: coupon.valid_until,
    conditions: coupon.conditions, welcome: coupon.welcome, active: coupon.active, headline: coupon.headline || "",
  } : EMPTY);
  const [imageAi, setImageAi] = useState(coupon?.image_ai || "");
  const [useAi, setUseAi] = useState(coupon?.use_ai || false);
  const [style, setStyle] = useState("colorido");
  const [hint, setHint] = useState("");
  const [saving, setSaving] = useState(false);
  const [designing, setDesigning] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [savedId, setSavedId] = useState(coupon?.coupon_id || null);
  const fileRef = useRef(null);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const preview = {
    ...form,
    image: useAi && imageAi ? imageAi : form.image,
    discount_label: form.discount_type === "percent" ? `${form.discount_value || 0}% OFF` : form.discount_type === "amount" ? `-$${Number(form.discount_value || 0).toLocaleString("es-MX")} MXN` : "GRATIS",
    estimated_saving: form.discount_type === "percent" ? (Number(form.product_price || 0) * Number(form.discount_value || 0)) / 100 : form.discount_type === "amount" ? Number(form.discount_value || 0) : Number(form.product_price || 0),
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    try { upd("image", await compressImage(f)); setImageAi(""); setUseAi(false); }
    catch (err) { toast.error(err.message); }
    finally { setUploading(false); }
  };

  const payload = () => ({
    ...form,
    discount_value: Number(form.discount_value) || 0,
    product_price: form.product_price === "" ? null : Number(form.product_price),
  });

  const save = async (silent = false) => {
    setSaving(true);
    try {
      const { data } = savedId
        ? await api.put(`/merchants/me/coupons/${savedId}`, payload())
        : await api.post("/merchants/me/coupons", payload());
      setSavedId(data.coupon_id);
      if (!silent) { toast.success("Cupón guardado"); onSaved?.(); }
      return data.coupon_id;
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Error al guardar");
      return null;
    } finally { setSaving(false); }
  };

  const design = async () => {
    if (!form.title.trim()) return toast.error("Primero escribe el producto o servicio del cupón");
    setDesigning(true);
    try {
      const id = await save(true);
      if (!id) return;
      const { data } = await api.post(`/merchants/me/coupons/${id}/design`, { style, extra_hint: hint });
      setImageAi(data.image_ai); setUseAi(true);
      onQuota?.(data.quota);
      toast.success("¡Diseño listo! Revisa la vista previa");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "El diseño IA falló");
    } finally { setDesigning(false); }
  };

  const toggleAi = async (v) => {
    if (!savedId) return;
    try { await api.post(`/merchants/me/coupons/${savedId}/design/toggle`, { use_ai: v }); setUseAi(v); } catch { toast.error("No pude cambiar la versión"); }
  };

  const remaining = quota?.remaining ?? 0;

  return (
    <div className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm overflow-y-auto" data-testid="coupon-editor">
      <div className="min-h-full flex items-start justify-center px-4 py-8">
        <div className="w-full max-w-5xl rounded-3xl bg-white shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">{savedId ? "Editar cupón" : "Nuevo cupón"}</div>
              <h2 className="font-heading font-extrabold text-xl">{form.title || "Diseña tu cupón"}</h2>
            </div>
            <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-neutral-100 grid place-items-center" aria-label="Cerrar" data-testid="coupon-editor-close"><X className="w-4 h-4" /></button>
          </div>

          <div className="grid lg:grid-cols-[1fr_360px] gap-6 p-6">
            <div className="space-y-5">
              <StrategyTip />
              <CouponFields form={form} upd={upd} />
              <AiDesignBox form={form} quota={quota} fileRef={fileRef} onFile={onFile} uploading={uploading} style={style} setStyle={setStyle} hint={hint} setHint={setHint}
                remaining={remaining} designing={designing} onDesign={design} imageAi={imageAi} useAi={useAi} onToggleAi={toggleAi} />
              <div className="flex items-center justify-between gap-3 pt-2">
                {savedId && onSaved && (
                  <Button type="button" variant="ghost" onClick={async () => { if (!window.confirm("¿Eliminar este cupón? Los cupones ya entregados quedarán como vencidos.")) return; await api.delete(`/merchants/me/coupons/${savedId}`); toast.success("Cupón eliminado"); onSaved(); onClose(); }} className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full" data-testid="coupon-delete">
                    <Trash2 className="w-4 h-4" /> Eliminar
                  </Button>
                )}
                <Button type="button" onClick={() => save()} disabled={saving} className="ml-auto rounded-full bg-black hover:bg-neutral-800 text-white font-bold h-11 px-6" data-testid="coupon-save">
                  <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar cupón"}
                </Button>
              </div>
            </div>
            <PreviewPane preview={preview} merchant={merchant} designing={designing} />
          </div>
        </div>
      </div>
    </div>
  );
}

function StrategyTip() {
  return (
    <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4" data-testid="coupon-strategy-tip">
      <div className="flex items-center gap-2 text-amber-800">
        <Lightbulb className="w-4 h-4 shrink-0" />
        <div className="text-[11px] font-black uppercase tracking-widest">Recomendación para eficientar resultados con el uso de cupones de descuento</div>
      </div>
      <p className="text-sm text-amber-900 mt-2 leading-relaxed">
        El marketing que te proponemos es el mismo que utilizan las tiendas de autoservicio y departamentales: <strong>atraer clientes con una oferta gancho</strong> y de esa manera venderle al cliente otros productos o servicios a precios normales. Para tal efecto recomendamos lo siguiente:
      </p>
      <ol className="mt-2 space-y-1.5 text-sm text-amber-900 list-decimal pl-5">
        <li>Busca un producto o servicio que pueda de <strong>VERDAD impactar con su precio</strong> gracias al cupón (aunque lo tengas que dar al costo).</li>
        <li>Ese producto o servicio dentro de tu tienda o local <strong>NO debe estar en descuento u oferta</strong>, para crear en tu cliente sensación de exclusividad. <strong>Respeta siempre tu propuesta.</strong></li>
        <li>Haz sentir a ese cliente que es <strong>importante y valioso</strong> para tu negocio.</li>
      </ol>
    </div>
  );
}

function CouponFields({ form, upd }) {
  return (
    <>
    <div className="grid md:grid-cols-2 gap-4">
      <div className="md:col-span-2">
        <Label className="text-xs">1. Producto o servicio con descuento *</Label>
        <Input value={form.title} onChange={(e) => upd("title", e.target.value)} placeholder="Ej. Corte de cabello · Latte grande · Blusa de lino" className="mt-1 h-11 rounded-xl" maxLength={80} data-testid="coupon-title" />
      </div>
      <div>
        <Label className="text-xs">2. Tipo de descuento *</Label>
        <div className="mt-1 grid grid-cols-3 gap-1 rounded-xl bg-neutral-100 p-1">
          {[["percent", "% OFF"], ["amount", "$ MXN"], ["freebie", "GRATIS"]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => upd("discount_type", v)} data-testid={`coupon-type-${v}`}
              className={`h-9 rounded-lg text-xs font-bold transition-colors ${form.discount_type === v ? "bg-[#FF007A] text-white shadow" : "text-neutral-600 hover:bg-white"}`}>{l}</button>
          ))}
        </div>
      </div>
      {form.discount_type !== "freebie" && (
        <div>
          <Label className="text-xs">{form.discount_type === "percent" ? "Porcentaje (1-100)" : "Monto de descuento (MXN)"} *</Label>
          <Input type="number" min={1} value={form.discount_value} onChange={(e) => upd("discount_value", e.target.value)} className="mt-1 h-11 rounded-xl" data-testid="coupon-discount-value" />
        </div>
      )}
      <div>
        <Label className="text-xs">Precio normal del producto (MXN) {form.discount_type === "freebie" ? "*" : "— para mostrar el ahorro"}</Label>
        <Input type="number" min={0} step="0.01" value={form.product_price} onChange={(e) => upd("product_price", e.target.value)} placeholder="85.00" className="mt-1 h-11 rounded-xl" data-testid="coupon-product-price" />
      </div>
      <div>
        <Label className="text-xs">3. Vigencia — desde</Label>
        <Input type="date" value={form.valid_from} onChange={(e) => upd("valid_from", e.target.value)} className="mt-1 h-11 rounded-xl" data-testid="coupon-valid-from" />
      </div>
      <div>
        <Label className="text-xs">Vigencia — hasta</Label>
        <Input type="date" value={form.valid_until} onChange={(e) => upd("valid_until", e.target.value)} className="mt-1 h-11 rounded-xl" data-testid="coupon-valid-until" />
      </div>
      <div className="md:col-span-2">
        <Label className="text-xs">4. Condiciones de uso</Label>
        <Textarea value={form.conditions} onChange={(e) => upd("conditions", e.target.value)} placeholder="Ej. Un cupón por persona · No acumulable · Presenta el código en caja · Válido en consumo mínimo de $150" className="mt-1 rounded-xl min-h-[70px]" maxLength={300} data-testid="coupon-conditions" />
      </div>
      <div className="md:col-span-2">
        <Label className="text-xs">Frase gancho (opcional)</Label>
        <Input value={form.headline} onChange={(e) => upd("headline", e.target.value)} placeholder="Ej. Tu antojo de la semana con precio de amigo ☕" className="mt-1 h-11 rounded-xl" maxLength={90} data-testid="coupon-headline" />
      </div>
    </div>

    <div className="grid md:grid-cols-2 gap-3">
      <label className="flex items-center justify-between gap-3 rounded-2xl border border-neutral-200 p-3 cursor-pointer">
        <div><div className="text-sm font-bold">Cupón de bienvenida</div><div className="text-[11px] text-neutral-500">Se entrega automático a cada nuevo cliente que escanea tu QR</div></div>
        <Switch checked={form.welcome} onCheckedChange={(v) => upd("welcome", v)} data-testid="coupon-welcome" />
      </label>
      <label className="flex items-center justify-between gap-3 rounded-2xl border border-neutral-200 p-3 cursor-pointer">
        <div><div className="text-sm font-bold">Activo</div><div className="text-[11px] text-neutral-500">Inactivo = deja de poder enviarse y canjearse</div></div>
        <Switch checked={form.active} onCheckedChange={(v) => upd("active", v)} data-testid="coupon-active" />
      </label>
    </div>
    </>
  );
}

function AiDesignBox({ form, quota, fileRef, onFile, uploading, style, setStyle, hint, setHint, remaining, designing, onDesign, imageAi, useAi, onToggleAi }) {
  return (
    <div className="rounded-3xl border-2 border-dashed border-[#FF007A]/40 bg-[#FFF0F7]/50 p-5">
      <div className="flex items-center gap-2 mb-1"><Wand2 className="w-5 h-5 text-[#FF007A]" /><h3 className="font-heading font-extrabold">5. Imagen y diseño con IA</h3></div>
      <p className="text-xs text-neutral-600 leading-relaxed">
        <strong>Cómo funciona:</strong> sube una foto real del producto o servicio (opcional pero recomendado). La IA conservará tu producto intacto y creará alrededor un arte de cupón profesional con tu logo, sin textos — el descuento, vigencia y condiciones se sobreponen automáticamente para que siempre se lean perfecto. Cada diseño consume 1 de tus <strong>{quota?.limit ?? 6} diseños mensuales</strong>.
      </p>
      <div className="mt-4 grid sm:grid-cols-[140px_1fr] gap-4">
        <div className="relative rounded-2xl overflow-hidden bg-white border border-neutral-200 aspect-square">
          {form.image ? <img src={form.image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-neutral-400 text-xs text-center px-2">Foto del producto</div>}
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="absolute bottom-2 left-2 right-2 bg-white/95 text-neutral-800 text-[11px] font-bold py-1.5 rounded-full flex items-center justify-center gap-1 shadow" data-testid="coupon-upload">
            <ImagePlus className="w-3.5 h-3.5" /> {uploading ? "Subiendo…" : form.image ? "Cambiar" : "Subir foto"}
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" />
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-4 gap-1.5">
            {STYLES.map((s) => (
              <button key={s.id} type="button" onClick={() => setStyle(s.id)} data-testid={`coupon-style-${s.id}`}
                className={`rounded-xl px-2 py-2 text-left border transition-colors ${style === s.id ? "border-[#FF007A] bg-white shadow" : "border-neutral-200 bg-white/60 hover:bg-white"}`}>
                <div className="text-xs font-bold">{s.label}</div><div className="text-[9px] text-neutral-500 leading-tight">{s.hint}</div>
              </button>
            ))}
          </div>
          <Input value={hint} onChange={(e) => setHint(e.target.value)} placeholder="Indicación extra para la IA (ej. fondo con flores, tonos pastel, ambiente navideño)" className="h-10 rounded-xl text-xs bg-white" maxLength={140} data-testid="coupon-ai-hint" />
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" onClick={onDesign} disabled={designing || remaining <= 0} className="rounded-full bg-[#FF007A] hover:bg-[#e6006e] font-bold h-10" data-testid="coupon-design-btn">
              {designing ? <><Loader2 className="w-4 h-4 animate-spin" /> Diseñando (10-20s)…</> : <><Sparkles className="w-4 h-4" /> {imageAi ? "Rediseñar con IA" : "Diseñar con IA"}</>}
            </Button>
            <span className={`text-[11px] font-bold ${remaining <= 0 ? "text-red-500" : "text-neutral-500"}`} data-testid="coupon-quota">{remaining} de {quota?.limit ?? 6} diseños disponibles este mes</span>
          </div>
          {imageAi && (
            <button type="button" onClick={() => onToggleAi(!useAi)} className="text-xs font-bold text-[#0055FF] flex items-center gap-1" data-testid="coupon-toggle-ai">
              <RotateCcw className="w-3 h-3" /> {useAi ? "Ver con mi foto original" : "Ver diseño IA"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PreviewPane({ preview, merchant, designing }) {
  return (
    <div className="lg:sticky lg:top-0 self-start">
      <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 mb-2">Vista previa — así lo verá tu cliente</div>
      <CouponCard coupon={preview} merchantName={merchant.business_name} code="CUP-XXXXXX" holeColor="#ffffff" testid="coupon-preview" />
      {designing && (
        <div className="mt-3 rounded-2xl bg-[#FFF0F7] border border-[#FF007A]/30 p-3 text-xs text-[#FF007A] font-bold flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> La IA está creando el arte de tu cupón…</div>
      )}
    </div>
  );
}
